import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { Dayjs } from 'dayjs'

import { Provider } from '../../../providers/ProvidersProvider/types'
import {
  ProviderTaskBarrier,
  ProviderTaskPriorityLevel,
  ProviderTaskResponse,
  ProviderTaskStatus,
  ProviderTaskType,
  UpdateProviderTaskRequest,
} from '../../../providers/ProviderTask/ProviderTask.provider'

interface EditProviderTaskModalProps {
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  showEditModal: boolean
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>
  selectedTask: ProviderTaskResponse | null

  taskTypes: ProviderTaskType[]
  providers: Provider[]
  priorityLevels: ProviderTaskPriorityLevel[]
  statuses: ProviderTaskStatus[]
  barriers: ProviderTaskBarrier[]
  formTaskTypeId: number | ''
  setFormTaskTypeId: React.Dispatch<React.SetStateAction<number>>
  formAssignedToProviderId: number
  setFormAssignedToProviderId: React.Dispatch<React.SetStateAction<number>>
  formPriorityLevelId: number | ''
  setFormPriorityLevelId: React.Dispatch<React.SetStateAction<number>>
  formStatusId: number | ''
  setFormStatusId: React.Dispatch<React.SetStateAction<number>>
  formBarrierId: number | null
  setFormBarrierId: React.Dispatch<React.SetStateAction<number | null>>
  formDueAt: Dayjs | null
  setFormDueAt: React.Dispatch<React.SetStateAction<Dayjs | null>>
  onUpdateTask: (request: UpdateProviderTaskRequest) => Promise<any>
  setOptimisticTasks: React.Dispatch<
    React.SetStateAction<ProviderTaskResponse[]>
  >
}

export const EditProviderTaskModal = ({
  loading,
  setLoading,
  showEditModal,
  setShowEditModal,
  selectedTask,
  taskTypes,
  providers,
  priorityLevels,
  statuses,
  barriers,
  formTaskTypeId,
  setFormTaskTypeId,
  formAssignedToProviderId,
  setFormAssignedToProviderId,
  formPriorityLevelId,
  setFormPriorityLevelId,
  formStatusId,
  setFormStatusId,
  formBarrierId,
  setFormBarrierId,
  formDueAt,
  setFormDueAt,
  onUpdateTask,
  setOptimisticTasks,
}: EditProviderTaskModalProps) => {
  const handleUpdateTask = async () => {
    setLoading(true)

    if (
      !selectedTask ||
      !formTaskTypeId ||
      !formAssignedToProviderId ||
      !formPriorityLevelId ||
      !formStatusId ||
      !formDueAt
    )
      return

    const taskType = taskTypes.find((t) => t.id === formTaskTypeId)
    const assignedProvider = providers.find(
      (p) => p.providerId === formAssignedToProviderId
    )
    const priorityLevel = priorityLevels.find(
      (p) => p.id === formPriorityLevelId
    )
    const status = statuses.find((s) => s.id === formStatusId)
    const barrier = formBarrierId
      ? barriers.find((b) => b.id === formBarrierId) ?? null
      : null

    if (!taskType || !assignedProvider || !priorityLevel || !status) return

    const previousTask = selectedTask // snapshot for rollback

    const optimisticTask: ProviderTaskResponse = {
      ...selectedTask,
      providerTaskType: taskType,
      assignedToProviderId: formAssignedToProviderId,
      assignedToProvider: assignedProvider.name,
      priorityLevel,
      status,
      barrier,
      dueAt: formDueAt.toISOString(),
    }

    // Update immediately, close modal right away
    setOptimisticTasks((prev) =>
      prev.map((t) => (t.id === selectedTask.id ? optimisticTask : t))
    )

    try {
      await onUpdateTask({
        providerTaskId: selectedTask.id,
        providerTaskTypeId: formTaskTypeId as number,
        assignedToProviderId: formAssignedToProviderId as number,
        providerTaskPriorityLevelId: formPriorityLevelId as number,
        providerTaskStatusId: formStatusId as number,
        providerTaskBarrierId: formBarrierId,
        dueAt: formDueAt.toISOString(),
      })
      setShowEditModal(false)
    } catch (error) {
      console.log('onUpdateTask-error', error)
      // Rollback to the pre-edit task
      setOptimisticTasks((prev) =>
        prev.map((t) => (t.id === selectedTask.id ? previousTask : t))
      )
      setShowEditModal(true) // reopen so they can retry without losing form state
      alert('Failed to update task. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={showEditModal}
      onClose={() => setShowEditModal(false)}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle fontSize={18}>Edit Task</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
      >
        <FormControl size='small' fullWidth sx={{ mt: 1 }}>
          <InputLabel>Task Type</InputLabel>
          <Select
            value={formTaskTypeId}
            label='Task Type'
            onChange={(e) => setFormTaskTypeId(e.target.value as number)}
          >
            {taskTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          size='medium'
          sx={{ marginY: 1, fontSize: 16 }}
          fullWidth
          options={providers}
          getOptionLabel={(option) => `${option.name}`}
          value={
            providers.find((p) => p.providerId === formAssignedToProviderId) ??
            null
          }
          isOptionEqualToValue={(option, value) =>
            option.providerId === value.providerId
          }
          onChange={(_, value) =>
            setFormAssignedToProviderId(value?.providerId ?? 0)
          }
          renderOption={(props, option) => (
            <li {...props} key={option.providerId}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label='Assign to Provider' />
          )}
        />

        <FormControl size='small' fullWidth>
          <InputLabel>Priority Level</InputLabel>
          <Select
            value={formPriorityLevelId}
            label='Priority Level'
            onChange={(e) => setFormPriorityLevelId(e.target.value as number)}
          >
            {priorityLevels.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={formStatusId}
            label='Status'
            onChange={(e) => setFormStatusId(e.target.value as number)}
          >
            {statuses.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' fullWidth>
          <InputLabel>Barrier (Optional)</InputLabel>
          <Select
            value={formBarrierId ?? ''}
            label='Barrier (Optional)'
            onChange={(e) =>
              setFormBarrierId(
                e.target.value ? (e.target.value as number) : null
              )
            }
          >
            <MenuItem value=''>None</MenuItem>
            {barriers.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.barrier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DatePicker
          label='Due Date'
          value={formDueAt}
          onChange={(date) => setFormDueAt(date)}
          renderInput={(params: any) => (
            <TextField {...params} size='small' fullWidth />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setShowEditModal(false)} sx={{ fontSize: 15 }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleUpdateTask}
          sx={{ fontSize: 15 }}
        >
          {loading ? <CircularProgress size={20} color='secondary' /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
